import { useMemo } from 'react';
import currencyCodes from 'currency-codes';
import { useDictStore } from '@/stores/dictStore';
import { useDict } from '@/hooks/useDict';

export interface CurrencyOption {
  label: string;
  value: string;
}

/**
 * 根据货币代码（如 CNY, USD, EUR 等）使用 currency-codes 校验并格式化价格
 */
export function formatCurrencyPrice(
  amount: number | string | undefined | null,
  currencyInput?: string
): string {
  if (amount === undefined || amount === null) return '面议';
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(numericAmount)) return '面议';

  const codeStr = (currencyInput || 'CNY').trim().toUpperCase();

  let isoCode = codeStr;
  if (codeStr === '￥' || codeStr === 'RMB') isoCode = 'CNY';
  if (codeStr === '$') isoCode = 'USD';
  if (codeStr === '€') isoCode = 'EUR';
  if (codeStr === '£') isoCode = 'GBP';

  // 使用 currency-codes 查找 ISO 4217 货币记录
  const currencyRecord = currencyCodes.code(isoCode);
  const targetCurrency = currencyRecord ? currencyRecord.code : isoCode;

  try {
    const formatter = new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: targetCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: currencyRecord ? currencyRecord.digits : 2
    });
    return formatter.format(numericAmount);
  } catch {
    const symbol = getCurrencySymbol(targetCurrency);
    const formattedNum = Number.isInteger(numericAmount)
      ? numericAmount.toString()
      : numericAmount.toLocaleString('zh-CN');
    return `${symbol}${formattedNum}`;
  }
}

/**
 * 使用 currency-codes 获取货币符号
 */
export function getCurrencySymbol(currencyInput?: string): string {
  const codeStr = (currencyInput || 'CNY').trim().toUpperCase();
  let isoCode = codeStr;
  if (codeStr === '￥' || codeStr === 'RMB') isoCode = 'CNY';
  if (codeStr === '$') isoCode = 'USD';
  if (codeStr === '€') isoCode = 'EUR';
  if (codeStr === '£') isoCode = 'GBP';

  const record = currencyCodes.code(isoCode);
  const targetCode = record ? record.code : isoCode;

  try {
    const parts = new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: targetCode
    }).formatToParts(1);
    const currencyPart = parts.find((p) => p.type === 'currency');
    return currencyPart ? currencyPart.value : targetCode;
  } catch {
    return targetCode === 'CNY' ? '￥' : targetCode === 'USD' ? '$' : targetCode;
  }
}

/**
 * 获取货币下拉选项列表 (优先从字典 key="CURRENCY" 中获取，找不到或未加载时退回至默认/ISO 4217 列表)
 */
export function getCurrencyOptions(): CurrencyOption[] {
  // 优先从字典 Store 获取 CURRENCY 类型的字典项
  const dictItems = useDictStore.getState().getDictItems('CURRENCY');
  if (dictItems && dictItems.length > 0) {
    return dictItems.map((item) => ({
      label: item.item_name || item.item_code,
      value: item.item_code || item.item_value
    }));
  }

  const priorityCodes = ['CNY', 'USD', 'EUR', 'HKD', 'GBP', 'JPY', 'CAD', 'AUD', 'SGD'];
  const allRecords = currencyCodes.data || [];

  const codeSet = new Set<string>();
  const options: CurrencyOption[] = [];

  const addCode = (code: string) => {
    const uppercaseCode = code.trim().toUpperCase();
    if (!uppercaseCode || codeSet.has(uppercaseCode) || uppercaseCode === 'XXX') return;
    codeSet.add(uppercaseCode);
    const symbol = getCurrencySymbol(uppercaseCode);
    const label =
      symbol && symbol !== uppercaseCode ? `${uppercaseCode} (${symbol})` : uppercaseCode;
    options.push({ label, value: uppercaseCode });
  };

  // 1. 优先添加常用热门货币
  priorityCodes.forEach(addCode);

  // 2. 动态加入所有 ISO 4217 货币
  const sortedRecords = [...allRecords].sort((a, b) => a.code.localeCompare(b.code));
  sortedRecords.forEach((record) => addCode(record.code));

  return options;
}

/**
 * 异步从字典 key="CURRENCY" 中拉取最新的货币类型下拉选项列表
 */
export async function fetchCurrencyOptions(): Promise<CurrencyOption[]> {
  try {
    const items = await useDictStore.getState().fetchDict('CURRENCY');
    if (items && items.length > 0) {
      return items.map((item) => ({
        label: item.item_name || item.item_code,
        value: item.item_code || item.item_value
      }));
    }
  } catch {
    // 失败降级
  }
  return getCurrencyOptions();
}

/**
 * 在 React 组件中快捷使用 CURRENCY 货币字典选项的 Hook
 */
export function useCurrencyOptions(): { options: CurrencyOption[]; loading: boolean } {
  const { items, loading } = useDict('CURRENCY');

  const options = useMemo(() => {
    if (items && items.length > 0) {
      return items.map((item) => ({
        label: item.item_name || item.item_code,
        value: item.item_code || item.item_value
      }));
    }
    return getCurrencyOptions();
  }, [items]);

  return { options, loading };
}

/** 动态导出的货币下拉选项 */
export const CURRENCY_OPTIONS: CurrencyOption[] = getCurrencyOptions();
